import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Calendar, AlarmClock, ChevronDown, DollarSign, Zap, ChevronRight,
} from 'lucide-react';
import type { WizardMode } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import {
  getCampaignPackage,
  splitContractAmount,
  suggestedPackagePrice,
} from '@/lib/campaignPackages';

const FESTIVAL_QUICK_PICKS = [
  { value: '1weekend', label: '1 weekend', days: 3 },
  { value: '1week', label: '1 week', days: 7 },
  { value: '2weeks', label: '2 weeks', days: 14 },
  { value: 'run_of_festival', label: 'Run of festival (3w)', days: 21 },
];

const STANDARD_PRESETS = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

const todayIso = () => new Date().toISOString().split('T')[0];

function diffDays(start: string, end: string): number {
  if (!start || !end) return 0;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(1, Math.round(ms / 86400000));
}

function addDays(start: string, days: number): string {
  return new Date(new Date(start).getTime() + days * 86400000)
    .toISOString()
    .split('T')[0];
}

function fmtLong(iso: string): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface ScheduleStepProps {
  mode: WizardMode;
}

export default function ScheduleStep({ mode }: ScheduleStepProps) {
  const store = useWizardStore(mode);
  const { seesMoney } = useAdminAuth();
  const {
    startDate,
    endDate,
    durationDays,
    festivalMode,
    festivalQuickPick,
    bidXcd,
    cpmRateXcd,
    flatFeeXcd,
    maxImpressionsPerDayPerUser,
    coolDownHours,
    invoiceStatus,
    invoiceAmountXcd,
    campaignTypes,
    packageKey,
  } = store;

  const pkg = getCampaignPackage(packageKey);
  const suggestedPrice = pkg ? suggestedPackagePrice(pkg, durationDays) : null;
  const contractTotal = parseFloat(invoiceAmountXcd);
  const placementCount = Math.max(1, campaignTypes.length);
  const perPlacement = splitContractAmount(contractTotal, placementCount);

  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Keep durationDays in sync with start+end
  useEffect(() => {
    if (startDate && endDate) {
      const d = diffDays(startDate, endDate);
      if (d !== durationDays) store.set({ durationDays: d });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleStartChange = (newStart: string) => {
    if (!newStart) return;
    const d = durationDays || 30;
    store.set({ startDate: newStart, endDate: addDays(newStart, d) });
  };

  const handleEndChange = (newEnd: string) => {
    if (!newEnd) return;
    const d = diffDays(startDate, newEnd);
    if (d < 1) return; // ignore — would invert the range
    store.set({ endDate: newEnd, durationDays: d });
  };

  const handleDurationChange = (days: number) => {
    const safe = Math.max(1, Math.min(365, days));
    store.set({
      durationDays: safe,
      endDate: addDays(startDate || todayIso(), safe),
    });
  };

  const presets = festivalMode || mode === 'festival' ? FESTIVAL_QUICK_PICKS : STANDARD_PRESETS;

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-white">Schedule</h2>

      {/* Auto-kill warning — front and center */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2.5">
        <AlarmClock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-200 leading-relaxed">
          <span className="font-semibold text-amber-300">Auto-kill switch:</span>{' '}
          this campaign will be pulled from the app automatically at{' '}
          <span className="font-mono text-amber-300">11:59 PM Dominica time</span>{' '}
          on the End Date below, so no manual takedown is needed. Start and end are
          anchored to Dominica (AST), so it's correct even if you schedule from
          abroad. Set the right end date and forget it.
        </div>
      </div>

      {/* Start + end calendars */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-gray-300 text-sm flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> Start date
          </Label>
          <Input
            type="date"
            value={startDate}
            min={todayIso()}
            onChange={(e) => handleStartChange(e.target.value)}
            className="bg-gray-900 border-gray-700 text-white"
          />
          <p className="text-[11px] text-gray-500">{fmtLong(startDate)}</p>
        </div>
        <div className="space-y-1">
          <Label className="text-gray-300 text-sm flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> End date
          </Label>
          <Input
            type="date"
            value={endDate}
            min={startDate || todayIso()}
            onChange={(e) => handleEndChange(e.target.value)}
            className="bg-gray-900 border-gray-700 text-white"
          />
          <p className="text-[11px] text-amber-400">
            Pulled from app at 11:59 PM on {fmtLong(endDate)}
          </p>
        </div>
      </div>

      {/* Duration display + slider for long ranges */}
      <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-300">
            Total flight:{' '}
            <span className="text-emerald-400 font-semibold">
              {durationDays} day{durationDays === 1 ? '' : 's'}
            </span>
          </span>
          <Input
            type="number"
            min={1}
            max={365}
            value={durationDays}
            onChange={(e) => handleDurationChange(parseInt(e.target.value) || 1)}
            className="bg-gray-900 border-gray-700 text-white w-20 h-8 text-center"
          />
        </div>
        <Slider
          value={[durationDays]}
          min={1}
          max={120}
          step={1}
          onValueChange={([v]) => handleDurationChange(v)}
        />
      </div>

      {/* Quick-pick chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500">Quick:</span>
        {presets.map((p) => {
          const isFestivalPreset = 'value' in p;
          const active = isFestivalPreset
            ? festivalQuickPick === (p as any).value
            : durationDays === p.days;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                handleDurationChange(p.days);
                if (isFestivalPreset) {
                  store.set({ festivalQuickPick: (p as any).value });
                }
              }}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                active
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Invoice */}
      <div className="space-y-3 border-t border-gray-800 pt-5">
        <Label className="text-gray-300 font-medium flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Contract amount
        </Label>
        <p className="text-[11px] text-gray-500">
          The one number that matters: what the client agreed to pay (offline)
          for this whole campaign, in XCD. The status just helps you track
          collections. You can ignore "Advanced delivery" below unless you know
          you need per-impression bidding.
          {!seesMoney && ' A director marks it paid once the money arrives.'}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-gray-500 text-xs">Contract amount (XCD)</Label>
            <Input
              value={invoiceAmountXcd}
              onChange={(e) => store.set({ invoiceAmountXcd: e.target.value })}
              className="bg-gray-900 border-gray-700 text-white"
              placeholder="e.g. 1500"
              inputMode="decimal"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-gray-500 text-xs">Status</Label>
            <div className="flex gap-1.5">
              {(seesMoney ? ['pending', 'invoiced', 'paid'] : ['pending', 'invoiced']).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => store.set({ invoiceStatus: s })}
                  className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                    invoiceStatus === s
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        {pkg && suggestedPrice != null && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-3 py-2">
            <p className="text-xs text-emerald-200">
              {pkg.name} for {durationDays} day{durationDays === 1 ? '' : 's'}: suggested{' '}
              <span className="font-semibold tabular-nums">EC${suggestedPrice.toLocaleString()}</span>
              {durationDays !== pkg.days && (
                <span className="text-emerald-200/60"> (EC${pkg.priceXcd.toLocaleString()} per {pkg.days} days)</span>
              )}
            </p>
            {String(suggestedPrice) !== invoiceAmountXcd && (
              <button
                type="button"
                onClick={() => store.set({ invoiceAmountXcd: String(suggestedPrice) })}
                className="shrink-0 rounded-md border border-emerald-500/40 px-2.5 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/10"
              >
                Use this price
              </button>
            )}
          </div>
        )}
        {placementCount > 1 && contractTotal > 0 && (
          <p className="text-[11px] text-gray-500">
            Booked as {placementCount} campaigns of about{' '}
            <span className="text-gray-300 tabular-nums">EC${perPlacement[perPlacement.length - 1].toLocaleString()}</span>{' '}
            each, so revenue reports show the EC${contractTotal.toLocaleString()} total once.
          </p>
        )}
      </div>

      {/* Advanced delivery — collapsed by default */}
      <div className="border-t border-gray-800 pt-5">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"
        >
          {advancedOpen ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <Zap className="w-4 h-4" />
          Advanced delivery (frequency caps & bid)
          {!advancedOpen && (
            <Badge
              variant="outline"
              className="text-[10px] border-gray-700 text-gray-500 ml-1"
            >
              Optional
            </Badge>
          )}
        </button>

        {advancedOpen && (
          <div className="mt-3 space-y-4 bg-gray-900/40 border border-gray-800 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-500 text-xs">
                    Per-user/day cap
                  </Label>
                  <span className="text-xs text-emerald-400 font-mono">
                    {maxImpressionsPerDayPerUser}
                  </span>
                </div>
                <Slider
                  value={[maxImpressionsPerDayPerUser]}
                  min={1}
                  max={20}
                  step={1}
                  onValueChange={([v]) =>
                    store.set({ maxImpressionsPerDayPerUser: v })
                  }
                />
                <p className="text-[10px] text-gray-600">
                  How many times one person can see this ad in a single day
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-500 text-xs">
                    Cool-down (hrs)
                  </Label>
                  <span className="text-xs text-emerald-400 font-mono">
                    {coolDownHours}h
                  </span>
                </div>
                <Slider
                  value={[coolDownHours]}
                  min={1}
                  max={168}
                  step={1}
                  onValueChange={([v]) => store.set({ coolDownHours: v })}
                />
                <p className="text-[10px] text-gray-600">
                  Minimum gap after a user dismisses the ad
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-800">
              <div className="space-y-1">
                <Label className="text-gray-500 text-xs">Bid (XCD)</Label>
                <Input
                  value={bidXcd}
                  onChange={(e) => store.set({ bidXcd: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="0.10"
                />
                <p className="text-[10px] text-gray-600">Per-impression bid</p>
              </div>
              <div className="space-y-1">
                <Label className="text-gray-500 text-xs">CPM (XCD)</Label>
                <Input
                  value={cpmRateXcd}
                  onChange={(e) => store.set({ cpmRateXcd: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-500 text-xs">Flat fee (XCD)</Label>
                <Input
                  value={flatFeeXcd}
                  onChange={(e) => store.set({ flatFeeXcd: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
